import * as auth from '../../app/modules/auth/redux/AuthRedux'
import {refreshToken as requestRefreshToken} from '../../app/modules/auth/redux/AuthCRUD'

type RefreshQueueItem = {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

export default function setupAxios(axios: any, store: any) {
  axios.defaults.headers.Accept = 'application/json'

  let isRefreshing = false
  let refreshQueue: RefreshQueueItem[] = []
  let isRedirectingToRoot = false

  const processRefreshQueue = (error?: unknown, token?: string) => {
    refreshQueue.forEach((queuedRequest) => {
      if (error) {
        queuedRequest.reject(error)
        return
      }

      queuedRequest.resolve(token ?? '')
    })

    refreshQueue = []
  }

  const handleSessionExpired = () => {
    const hasSessionExpired = Boolean(store.getState()?.auth?.sessionExpired)
    if (hasSessionExpired) {
      return
    }

    store.dispatch(auth.actions.setSessionExpired(true))
  }

  const redirectToRoot = () => {
    if (typeof window === 'undefined' || isRedirectingToRoot) {
      return false
    }

    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/+$/, '')
    const rootPath = `${publicUrl || ''}/`
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`

    if (currentPath === rootPath) {
      return false
    }

    isRedirectingToRoot = true
    window.location.assign(rootPath)
    return true
  }

  axios.interceptors.request.use(
    (config: any) => {
      const {
        auth: {accessToken},
      } = store.getState()

      config.headers = config.headers ?? {}

      if (accessToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const originalRequest = error?.config as any
      const status = error?.response?.status as number | undefined

      if (status === 403) {
        if (redirectToRoot()) {
          return new Promise(() => {})
        }

        return Promise.reject(error)
      }

      if (!originalRequest || status !== 401) {
        return Promise.reject(error)
      }

      const requestUrl = String(originalRequest.url ?? '')
      const isRefreshCall = requestUrl.includes('/Authenticate/refresh-token')
      if (isRefreshCall || originalRequest.skipAuthRefresh || originalRequest._retry) {
        return Promise.reject(error)
      }

      const state = store.getState()
      const accessToken = state?.auth?.accessToken as string | undefined
      const hasAuthorization = Boolean(originalRequest?.headers?.Authorization ?? accessToken)

      if (!hasAuthorization) {
        return Promise.reject(error)
      }

      const currentRefreshToken =
        (state?.auth?.refreshToken as string | undefined) ??
        (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('refresh-token') ?? undefined : undefined)

      if (!currentRefreshToken) {
        handleSessionExpired()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          refreshQueue.push({resolve, reject})
        })
          .then((newAccessToken) => {
            originalRequest._retry = true
            originalRequest.headers = originalRequest.headers ?? {}
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
            return axios(originalRequest)
          })
          .catch((refreshError) => Promise.reject(refreshError))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const email = state?.auth?.user?.email as string | undefined
        const isRememberMe = Boolean(state?.auth?.isRememberMe)
        const {data} = await requestRefreshToken(currentRefreshToken, isRememberMe, email)

        const nextRefreshToken = data?.refreshToken
        const nextLongTermToken = data?.longTermToken
        const nextAccessToken = isRememberMe && nextLongTermToken ? nextLongTermToken : data?.token
        const nextDisplayName = data?.name ?? state?.auth?.displayName

        if (!nextAccessToken || !nextRefreshToken) {
          throw new Error('Token refresh response was incomplete.')
        }

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('refresh-token', nextRefreshToken)
        }

        if (typeof localStorage !== 'undefined') {
          if (isRememberMe && nextLongTermToken) {
            localStorage.setItem(
              'remember-me',
              JSON.stringify({
                token: nextLongTermToken,
                refreshToken: nextRefreshToken,
                displayName: nextDisplayName,
                longTermToken: nextLongTermToken,
              })
            )
          } else if (!isRememberMe) {
            localStorage.removeItem('remember-me')
          }
        }

        store.dispatch(
          auth.actions.setUser(
            undefined,
            nextAccessToken,
            nextRefreshToken,
            isRememberMe,
            nextLongTermToken,
            nextDisplayName
          )
        )

        processRefreshQueue(undefined, nextAccessToken)

        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`
        return axios(originalRequest)
      } catch (refreshError) {
        processRefreshQueue(refreshError)
        handleSessionExpired()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
  )
}
